// English and Kiswahili (FR-73); locale files come from the brand pack so a
// rebrand can change wording without code edits.
import i18next, { type i18n } from "i18next";
import { initReactI18next } from "react-i18next";
import type { PublicBrand } from "../brand/brand";

const STORAGE_KEY = "lng";

function storedLanguage(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function rememberLanguage(lng: string) {
  try {
    window.localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    // Storage may be unavailable (private mode); the choice lasts for this page only.
  }
}

export async function initI18n(brand: PublicBrand, fetchImpl: typeof fetch = fetch): Promise<i18n> {
  const resources: Record<string, { translation: Record<string, unknown> }> = {};
  await Promise.all(
    brand.locales.map(async (lng) => {
      const res = await fetchImpl(`${import.meta.env.BASE_URL}locales/${lng}.json`);
      if (!res.ok) throw new Error(`locales/${lng}.json: HTTP ${res.status}`);
      resources[lng] = { translation: (await res.json()) as Record<string, unknown> };
    }),
  );
  const stored = storedLanguage();
  const instance = i18next.createInstance();
  await instance.use(initReactI18next).init({
    resources,
    lng: stored && brand.locales.includes(stored) ? stored : brand.default_locale,
    fallbackLng: brand.default_locale,
    supportedLngs: brand.locales,
    interpolation: { escapeValue: false, defaultVariables: { brand: brand.name } },
    returnNull: false,
  });
  instance.on("languageChanged", (lng) => {
    document.documentElement.lang = lng;
  });
  document.documentElement.lang = instance.language;
  return instance;
}
